import React from 'react';
import { withTenant } from '../tenant';
import {
  SyncOutlined,
  PlusOutlined,
  FileTextOutlined,
  CompassOutlined,
  EditOutlined,
  LinkOutlined,
  CopyOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  CloseOutlined,
  ReloadOutlined,
  LoadingOutlined,
  FolderOpenOutlined,
  FileSearchOutlined,
  MehOutlined,
  SmileOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

export function DocumentsPanel() {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [info, setInfo] = React.useState(null);
  const [infoTenantId, setInfoTenantId] = React.useState('');
  const [pathModalOpen, setPathModalOpen] = React.useState(false);
  const [browsePath, setBrowsePath] = React.useState('');
  const [browseEntries, setBrowseEntries] = React.useState([]);
  const [browseParent, setBrowseParent] = React.useState('');
  const [currentPath, setCurrentPath] = React.useState('');
  const [ingesting, setIngesting] = React.useState(false);
  const ingestAbortRef = React.useRef(null);
  const eventSourceRef = React.useRef(null);
  const [logLines, setLogLines] = React.useState([]);
  const logRef = React.useRef(null);
  const [configuredPaths, setConfiguredPaths] = React.useState([]);
  const [addOnly, setAddOnly] = React.useState(false);
  const [selectedPaths, setSelectedPaths] = React.useState([]);
  const [progressPercent, setProgressPercent] = React.useState(0);
  const [etaLabel, setEtaLabel] = React.useState('');
  const startTimeRef = React.useRef(0);
  const totalsRef = React.useRef({ current: 0, total: 0 });
  const [confirmIngestOpen, setConfirmIngestOpen] = React.useState(false);
  const [skippedFiles, setSkippedFiles] = React.useState([]);
  const [showSkippedFiles, setShowSkippedFiles] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const [uploadResult, setUploadResult] = React.useState(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [uploadFiles, setUploadFiles] = React.useState([]);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadTenantId, setUploadTenantId] = React.useState('');
  const [showUploadSection, setShowUploadSection] = React.useState(false);
  const [uploadStats, setUploadStats] = React.useState(null);
  const [uploadFilesList, setUploadFilesList] = React.useState([]);
  const [uploadFilesLoading, setUploadFilesLoading] = React.useState(false);
  const [uploadFilesError, setUploadFilesError] = React.useState('');
  const [deletingFile, setDeletingFile] = React.useState('');
  const [updatingVisibilityFile, setUpdatingVisibilityFile] = React.useState('');
  const [tenants, setTenants] = React.useState([]);
  const [tenantLoading, setTenantLoading] = React.useState(false);
  const [tenantError, setTenantError] = React.useState('');
  const [tenantForm, setTenantForm] = React.useState({
    name: '',
    displayName: '',
    logoUrl: '',
    logoBase64: '',
    databaseName: '',
    isDev: false,
    responseTone: 'neutrale',
  });
  const [editOpen, setEditOpen] = React.useState(false);
  const [editTenantId, setEditTenantId] = React.useState('');
  const [editForm, setEditForm] = React.useState({
    name: '',
    displayName: '',
    logoUrl: '',
    logoBase64: '',
    enabled: true,
    responseTone: 'neutrale',
  });
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteTenantId, setDeleteTenantId] = React.useState('');
  const [deleteConfirmInput, setDeleteConfirmInput] = React.useState('');

  const allowedExtensions = React.useMemo(() => new Set(['.pdf', '.docx', '.xlsx', '.xls']), []);
  const MAX_LOGO_BYTES = 2 * 1024 * 1024;
  const allowedLogoTypes = React.useMemo(() => new Set(['image/png', 'image/jpeg', 'image/svg+xml']), []);
  const tenantLogoPreview = tenantForm.logoBase64 || tenantForm.logoUrl;
  const editLogoPreview = editForm.logoBase64 || editForm.logoUrl;
  const isTestEnvironmentHost = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    const host = (window.location.hostname || '').toLowerCase();
    if (!host) return false;
    if (host.endsWith('.azurewebsites.net')) return true;
    const firstLabel = host.split('.')[0] || '';
    return firstLabel.includes('-dev') || firstLabel.includes('-test');
  }, []);

  const getTenantAccessUrl = React.useCallback((tenant) => {
    if (!tenant?.id) return window.location.origin;
    if (isTestEnvironmentHost) {
      return `${window.location.origin}/?cod=${tenant.id}`;
    }
    if (tenant.databaseName) {
      return `https://${tenant.databaseName}.sportelloia.it`;
    }
    return `${window.location.origin}/?cod=${tenant.id}`;
  }, [isTestEnvironmentHost]);

  const readLogoFile = async (file) => {
    if (!file) {
      throw new Error('Seleziona un file valido.');
    }
    if (!allowedLogoTypes.has(file.type)) {
      throw new Error('Formato non valido. Solo JPG, PNG o SVG.');
    }
    if (file.size > MAX_LOGO_BYTES) {
      throw new Error('Dimensione massima logo 2MB.');
    }
    const reader = new FileReader();
    const dataUrl = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Errore lettura file.'));
      reader.readAsDataURL(file);
    });
    return typeof dataUrl === 'string' ? dataUrl : '';
  };

  const handleCreateLogoFile = async (file) => {
    try {
      const dataUrl = await readLogoFile(file);
      setTenantForm((prev) => ({ ...prev, logoBase64: dataUrl, logoUrl: '' }));
      setTenantError('');
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleEditLogoFile = async (file) => {
    try {
      const dataUrl = await readLogoFile(file);
      setEditForm((prev) => ({ ...prev, logoBase64: dataUrl, logoUrl: '' }));
      setTenantError('');
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : String(err));
    }
  };

  const addCurrentToSelection = () => {
    const p = browsePath?.trim();
    if (!p) return;
    setSelectedPaths((prev) => (prev.includes(p) ? prev : [...prev, p]));
  };
  const removeSelectedPath = (p) => {
    setSelectedPaths((prev) => prev.filter((x) => x !== p));
  };

  const removeConfiguredPath = async (path) => {
    if (!path) return;
    const ok = window.confirm(`Rimuovere il percorso?\n${path}`);
    if (!ok) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(withTenant(`/api/admin/config/paths?path=${encodeURIComponent(path)}`), { method: 'DELETE' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile rimuovere il percorso.');
      }
      setConfiguredPaths((prev) => prev.filter((p) => p !== path));
      await callInfo();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    // Esegui sempre Info all'apertura del tab (mount del componente)
    callInfo();
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callInfo = async (tenantId) => {
    const resolveUrl = (url) => withTenant(url, tenantId);
    setLoading(true);
    setMessage('');
    try {
      // Read configured paths (optional, used for ingestion only)
      const pathsRes = await fetch(resolveUrl('/api/admin/config/paths'), { method: 'GET' });
      const configured = pathsRes.ok ? await pathsRes.json().catch(() => []) : [];
      if (Array.isArray(configured)) {
        setConfiguredPaths(configured);
      } else {
        setConfiguredPaths([]);
      }

      // Fetch KB stats from database (connection health + ingestion state)
      const res = await fetch(resolveUrl('/api/admin/kb_stats'), { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Richiesta non riuscita');
      }
      const data = await res.json();
      setInfo(data?.stats ?? data);
      setInfoTenantId(tenantId || '');
      setMessage('Statistiche aggiornate.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadTenantStatsForUpload = async (tenantId) => {
    if (!tenantId) return;
    try {
      const res = await fetch(withTenant('/api/admin/kb_stats', tenantId), { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile caricare le statistiche del tenant.');
      }
      const data = await res.json();
      setUploadStats(data?.stats ?? data);
    } catch (err) {
      setUploadStats(null);
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const loadTenantFilesForUpload = async (tenantId) => {
    if (!tenantId) return;
    setUploadFilesLoading(true);
    setUploadFilesError('');
    try {
      const res = await fetch(withTenant('/api/files/list?limit=200&includeHidden=true', tenantId), { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile caricare la lista file.');
      }
      const data = await res.json();
      setUploadFilesList(Array.isArray(data) ? data : []);
    } catch (err) {
      setUploadFilesList([]);
      setUploadFilesError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadFilesLoading(false);
    }
  };

  const deleteUploadedFile = async (filePath, fileName) => {
    if (!filePath) return;
    const ok = window.confirm(`Eliminare il file "${fileName || filePath}"?\n\nQuesta operazione eliminerà:\n- Il file fisico\n- Tutti i chunk dal database\n- I metadata di ingestione`);
    if (!ok) return;
    setDeletingFile(filePath);
    setMessage('');
    try {
      const res = await fetch(withTenant(`/api/files?path=${encodeURIComponent(filePath)}`, uploadTenantId), { method: 'DELETE' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile eliminare il file.');
      }
      const data = await res.json();
      if (data.success) {
        setMessage(`File "${fileName || filePath}" eliminato.`);
        setUploadFilesList((prev) => prev.filter((f) => f.filePath !== filePath));
        await loadTenantStatsForUpload(uploadTenantId);
      } else {
        throw new Error('Eliminazione non riuscita.');
      }
    } catch (err) {
      setUploadFilesError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingFile('');
    }
  };

  const toggleFileVisibility = async (filePath, visible, fileName) => {
    if (!filePath) return;
    setUpdatingVisibilityFile(filePath);
    setUploadFilesError('');
    try {
      const res = await fetch(
        withTenant(`/api/files/visibility?path=${encodeURIComponent(filePath)}&visible=${visible}`, uploadTenantId),
        { method: 'PATCH' }
      );
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile aggiornare la visibilità del file.');
      }

      setUploadFilesList((prev) => {
        return prev.map((f) => (
          f.filePath === filePath
            ? { ...f, isVisible: visible }
            : f
        ));
      });
      setMessage(
        visible
          ? `File "${fileName || filePath}" reso visibile in elenco e fonti.`
          : `File "${fileName || filePath}" nascosto da elenco e fonti.`
      );
    } catch (err) {
      setUploadFilesError(err instanceof Error ? err.message : String(err));
    } finally {
      setUpdatingVisibilityFile('');
    }
  };

  const openTenantUpload = async (tenant) => {
    if (!tenant?.id) return;
    setUploadTenantId(tenant.id);
    setShowUploadSection(true);
    setUploadStats(null);
    setUploadFilesList([]);
    setUploadFilesError('');
    setMessage(`Caricamento documenti per tenant: ${tenant.displayName || tenant.name}`);
    await loadTenantStatsForUpload(tenant.id);
    await loadTenantFilesForUpload(tenant.id);
  };

  const loadTenants = async () => {
    setTenantLoading(true);
    setTenantError('');
    try {
      const res = await fetch('/api/admin/tenants', { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile caricare i tenant.');
      }
      const data = await res.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : String(err));
    } finally {
      setTenantLoading(false);
    }
  };

  const createTenant = async () => {
    setTenantLoading(true);
    setTenantError('');
    try {
      if (!tenantForm.name.trim() || !tenantForm.databaseName.trim()) {
        throw new Error('Nome e database name sono obbligatori.');
      }
      const payload = {
        name: tenantForm.name.trim(),
        displayName: tenantForm.displayName.trim() || undefined,
        logoUrl: tenantForm.logoUrl.trim() || undefined,
        logoBase64: tenantForm.logoBase64 || undefined,
        databaseName: tenantForm.databaseName.trim() || undefined,
        isDev: tenantForm.isDev || false,
        responseTone: tenantForm.responseTone || 'neutrale',
      };
      const res = await fetch('/api/admin/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Creazione tenant fallita.');
      }
      await res.json().catch(() => ({}));
      setTenantForm((prev) => ({
        ...prev,
        name: '',
        displayName: '',
        logoUrl: '',
        logoBase64: '',
        databaseName: '',
        isDev: false,
        responseTone: 'neutrale',
      }));
      await loadTenants();
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : String(err));
    } finally {
      setTenantLoading(false);
    }
  };

  const toggleTenant = async (id, enabled) => {
    setTenantLoading(true);
    setTenantError('');
    try {
      const res = await fetch(`/api/admin/tenants/${id}/enabled?enabled=${enabled}`, { method: 'PATCH' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile aggiornare il tenant.');
      }
      setTenants((prev) => prev.map((t) => (t.id === id ? { ...t, enabled } : t)));
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : String(err));
    } finally {
      setTenantLoading(false);
    }
  };

  const openEditTenant = (tenant) => {
    if (!tenant?.id) return;
    setEditTenantId(tenant.id);
    setEditForm({
      name: tenant.name || '',
      displayName: tenant.displayName || '',
      logoUrl: tenant.logoUrl || '',
      logoBase64: tenant.logoBase64 || '',
      enabled: tenant.enabled !== false,
      responseTone: tenant.responseTone || 'neutrale',
    });
    setEditOpen(true);
  };

  const openDeleteTenant = (tenant) => {
    if (!tenant?.id) return;
    setDeleteTenantId(tenant.id);
    setDeleteConfirmInput('');
    setDeleteOpen(true);
  };

  const confirmDeleteTenant = async () => {
    if (!deleteTenantId) return;
    setTenantLoading(true);
    setTenantError('');
    try {
      if (deleteConfirmInput.trim() !== deleteTenantId) {
        throw new Error('GUID non corrispondente. Operazione annullata.');
      }
      const res = await fetch(`/api/admin/tenants/${deleteTenantId}`, { method: 'DELETE' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile eliminare il tenant.');
      }
      setTenants((prev) => prev.filter((t) => t.id !== deleteTenantId));
      if (uploadTenantId === deleteTenantId) {
        setShowUploadSection(false);
        setUploadTenantId('');
        setUploadStats(null);
        setUploadFilesList([]);
      }
      setDeleteOpen(false);
      setDeleteTenantId('');
      setMessage('Tenant eliminato.');
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : String(err));
    } finally {
      setTenantLoading(false);
    }
  };

  const saveTenantEdits = async () => {
    if (!editTenantId) return;
    setTenantLoading(true);
    setTenantError('');
    try {
      if (!editForm.name.trim()) {
        throw new Error('Il nome del tenant è obbligatorio.');
      }
      const payload = {
        name: editForm.name.trim(),
        displayName: editForm.displayName.trim() || null,
        logoUrl: editForm.logoUrl.trim() || null,
        logoBase64: editForm.logoBase64 || null,
        enabled: !!editForm.enabled,
        responseTone: editForm.responseTone || 'neutrale',
      };
      setMessage('Salvataggio modifiche tenant...');

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 15000);
      let res;
      try {
        res = await fetch(`/api/admin/tenants/${editTenantId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!res.ok) {
        const raw = await res.text().catch(() => '');
        let detail = '';
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            detail = parsed?.detail || parsed?.title || '';
          } catch {
            detail = raw;
          }
        }
        throw new Error(detail || `Impossibile salvare le modifiche. (${res.status})`);
      }
      // Aggiorna UI e poi ricarica da backend per conferma persistenza
      setTenants((prev) =>
        prev.map((t) =>
          t.id === editTenantId
            ? {
                ...t,
                name: payload.name,
                displayName: payload.displayName || '',
                logoUrl: payload.logoUrl || '',
                logoBase64: payload.logoBase64 || '',
                enabled: payload.enabled,
                responseTone: payload.responseTone || 'neutrale',
              }
            : t
        )
      );
      await loadTenants();
      setEditOpen(false);
      setEditTenantId('');
      setMessage('Tenant aggiornato.');
    } catch (err) {
      if (err?.name === 'AbortError') {
        setTenantError('Timeout durante il salvataggio (DB o rete non raggiungibili).');
        return;
      }
      setTenantError(err instanceof Error ? err.message : String(err));
    } finally {
      setTenantLoading(false);
    }
  };

  const copyTenantId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setMessage('GUID tenant copiato negli appunti.');
    } catch {
      setMessage('Impossibile copiare il GUID.');
    }
  };

  const handleEmbeddingSchemaMismatch = async (payload, tenantId) => {
    const found = payload?.foundType || 'vector(1536)';
    const expected = payload?.expectedType || 'vector(3072)';
    const detail = payload?.detail || `Schema embedding non compatibile: trovato ${found}, atteso ${expected}.`;
    const confirmMessage = `${detail}\n\nVuoi aggiornare ora il database del tenant?\nATTENZIONE: tutti i dati già ingestionati verranno cancellati.`;
    const proceed = window.confirm(confirmMessage);
    if (!proceed) {
      setMessage('Migrazione schema annullata. Nessuna ingestione eseguita.');
      return false;
    }

    setLoading(true);
    try {
      const res = await fetch(withTenant('/api/admin/embedding_schema/upgrade', tenantId), { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.detail || body?.message || 'Aggiornamento schema non riuscito.');
      }

      await callInfo(tenantId);
      if (tenantId && tenantId === uploadTenantId) {
        await loadTenantStatsForUpload(tenantId);
        await loadTenantFilesForUpload(tenantId);
      }
      window.alert('Aggiornamento completato. La knowledge base del tenant è stata resettata al nuovo formato embedding. Ora avvia una nuova ingestion completa dei documenti.');
      setMessage('Schema KB aggiornato. Esegui una nuova ingestion completa.');
      return true;
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getToneMeta = (tone) => {
    const normalized = (tone || 'neutrale').toLowerCase();
    if (normalized === 'formale') {
      return {
        key: 'formale',
        label: 'Tono: formale',
        icon: <SafetyCertificateOutlined />,
      };
    }
    if (normalized === 'infrormale' || normalized === 'informale') {
      return {
        key: 'infrormale',
        label: 'Tono: informale',
        icon: <SmileOutlined />,
      };
    }
    return {
      key: 'neutrale',
      label: 'Tono: neutrale',
      icon: <MehOutlined />,
    };
  };

  const callAddPath = async () => {
    setAddOnly(true);
    setPathModalOpen(true);
    await fetchBrowse();
  };

  const fetchBrowse = async (path) => {
    try {
      const url = path ? `/api/admin/browse?path=${encodeURIComponent(path)}` : '/api/admin/browse';
      const res = await fetch(withTenant(url), { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile elencare la cartella.');
      }
      const data = await res.json();
      setBrowsePath(data.currentPath || '');
      setBrowseEntries(data.entries || []);
      setBrowseParent(data.parentPath || '');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const goInto = async (path) => {
    await fetchBrowse(path);
  };

  const confirmIngestWithPath = async () => {
    const selected = browsePath?.trim();
    setLoading(true);
    setMessage('');
    setPathModalOpen(false);
    try {
      if (addOnly) {
        const picks = selectedPaths.length > 0 ? selectedPaths : (selected ? [selected] : []);
        if (picks.length > 0) {
          const qs = picks.map(p => 'path=' + encodeURIComponent(p)).join('&');
          const addRes = await fetch(withTenant(`/api/admin/config/paths?${qs}`), { method: 'POST' });
          if (!addRes.ok) {
            const problem = await addRes.json().catch(() => ({}));
            throw new Error(problem.detail || 'Impossibile salvare il percorso.');
          }
          setMessage(picks.length > 1 ? 'Percorsi aggiunti.' : 'Percorso aggiunto.');
          setCurrentPath(picks[0]);
          await callInfo();
          setSelectedPaths([]);
        } else {
          setMessage('Nessun percorso selezionato.');
        }
        return;
      }

      setIngesting(true);
      setProgressPercent(0);
      setSkippedFiles([]);
      setShowSkippedFiles(false);
      if (ingestAbortRef.current) {
        try { ingestAbortRef.current.abort(); } catch {}
      }
      ingestAbortRef.current = new AbortController();

      // Open SSE for progress
      try {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        const es = new EventSource(withTenant('/api/admin/ingest_progress/stream'));
        eventSourceRef.current = es;
        setLogLines([]);
        es.onmessage = (e) => {
          setLogLines((prev) => {
            const next = [...prev, e.data];
            return next.slice(-500);
          });
          // Try to parse percent like "x/y (zz%) - file" or "Ingestion completata..."
          const m = e.data.match(/(\d+)\s*\/\s*(\d+)\s*\((\d+)%\)/);
          if (m && m[3]) {
            const pct = Math.max(0, Math.min(100, parseInt(m[3], 10)));
            setProgressPercent(pct);
            const now = Date.now();
            if (startTimeRef.current === 0) startTimeRef.current = now;
            totalsRef.current = { current: parseInt(m[1], 10), total: parseInt(m[2], 10) };
            const elapsed = (now - startTimeRef.current) / 1000; // seconds
            const done = totalsRef.current.current;
            if (done > 0) {
              const rate = elapsed / done;
              const remaining = (totalsRef.current.total - done) * rate;
              const mm = Math.floor(remaining / 60);
              const ss = Math.max(0, Math.floor(remaining % 60));
              setEtaLabel(`ETA ~ ${mm}:${ss.toString().padStart(2, '0')}`);
            }
          } else if (/Ingestion completata/i.test(e.data)) {
            setProgressPercent(100);
            setEtaLabel('');
            startTimeRef.current = 0;
            totalsRef.current = { current: 0, total: 0 };
          }
          if (logRef.current) {
            logRef.current.scrollTop = logRef.current.scrollHeight;
          }
        };
        es.onerror = () => {
          try { es.close(); } catch {}
          eventSourceRef.current = null;
        };
      } catch { /* ignore */ }

      // 1) Persist selected path to DB configuration
      const picks = selectedPaths.length > 0 ? selectedPaths : (selected ? [selected] : []);
      if (picks.length > 0) {
        const qs = picks.map(p => 'path=' + encodeURIComponent(p)).join('&');
        await fetch(withTenant(`/api/admin/config/paths?${qs}`), { method: 'POST' });
      }
      // 2) Read all configured and ingest all
      const pathsRes = await fetch(withTenant('/api/admin/config/paths'), { method: 'GET' });
      const configured = pathsRes.ok ? await pathsRes.json().catch(() => []) : [];
      let ingestUrl = '/api/admin/ingestdocfolder';
      if (Array.isArray(configured) && configured.length > 0) {
        const qs = configured.map(p => 'path=' + encodeURIComponent(p)).join('&');
        ingestUrl = `/api/admin/ingestdocfolder?${qs}`;
      } else if (picks.length > 0) {
        const qs = picks.map(p => 'path=' + encodeURIComponent(p)).join('&');
        ingestUrl = `/api/admin/ingestdocfolder?${qs}`;
      }

      const res = await fetch(withTenant(ingestUrl), { method: 'POST', signal: ingestAbortRef.current.signal });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        if (res.status === 409 && problem?.code === 'embedding_schema_mismatch') {
          await handleEmbeddingSchemaMismatch(problem);
          return;
        }
        throw new Error(problem.detail || problem.message || 'Richiesta non riuscita');
      }
      const data = await res.json();
      const skipped = data.skippedFiles || [];
      setSkippedFiles(skipped);
      setShowSkippedFiles(skipped.length > 0);
      setMessage(`Acquisizione completata. Elaborati: ${data.processed ?? 0}, Skippati: ${data.skipped ?? 0}.`);
      if (picks.length > 0) setCurrentPath(picks[0]);
      await callInfo();
      setSelectedPaths([]);
    } catch (err) {
      if (err?.name === 'AbortError') {
        setMessage('Acquisizione annullata.');
      } else {
        setMessage(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
      setIngesting(false);
      ingestAbortRef.current = null;
      try { eventSourceRef.current?.close(); } catch {}
      eventSourceRef.current = null;
    }
  };

  const startIngestionFromConfigured = async () => {
    setConfirmIngestOpen(false);
    setLoading(true);
    setMessage('');
    try {
      setIngesting(true);
      setProgressPercent(0);
      setSkippedFiles([]);
      setShowSkippedFiles(false);
      if (ingestAbortRef.current) {
        try { ingestAbortRef.current.abort(); } catch {}
      }
      ingestAbortRef.current = new AbortController();

      // Open SSE for progress
      try {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        const es = new EventSource(withTenant('/api/admin/ingest_progress/stream'));
        eventSourceRef.current = es;
        setLogLines([]);
        es.onmessage = (e) => {
          setLogLines((prev) => {
            const next = [...prev, e.data];
            return next.slice(-500);
          });
          const m = e.data.match(/(\d+)\s*\/\s*(\d+)\s*\((\d+)%\)/);
          if (m && m[3]) {
            const pct = Math.max(0, Math.min(100, parseInt(m[3], 10)));
            setProgressPercent(pct);
          } else if (/Ingestion completata/i.test(e.data)) {
            setProgressPercent(100);
          }
          if (logRef.current) {
            logRef.current.scrollTop = logRef.current.scrollHeight;
          }
        };
        es.onerror = () => {
          try { es.close(); } catch {}
          eventSourceRef.current = null;
        };
      } catch { /* ignore */ }

      // Build ingestion URL from configured paths (or default if empty)
      const pathsRes = await fetch(withTenant('/api/admin/config/paths'), { method: 'GET' });
      const configured = pathsRes.ok ? await pathsRes.json().catch(() => []) : [];
      let ingestUrl = '/api/admin/ingestdocfolder';
      if (Array.isArray(configured) && configured.length > 0) {
        const qs = configured.map(p => 'path=' + encodeURIComponent(p)).join('&');
        ingestUrl = `/api/admin/ingestdocfolder?${qs}`;
      }

      const res = await fetch(withTenant(ingestUrl), { method: 'POST', signal: ingestAbortRef.current.signal });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        if (res.status === 409 && problem?.code === 'embedding_schema_mismatch') {
          await handleEmbeddingSchemaMismatch(problem);
          return;
        }
        throw new Error(problem.detail || problem.message || 'Richiesta non riuscita');
      }
      const data = await res.json();
      const skipped = data.skippedFiles || [];
      setSkippedFiles(skipped);
      setShowSkippedFiles(skipped.length > 0);
      setMessage(`Acquisizione completata. Elaborati: ${data.processed ?? 0}, Skippati: ${data.skipped ?? 0}.`);
      await callInfo();
    } catch (err) {
      if (err?.name === 'AbortError') {
        setMessage('Acquisizione annullata.');
      } else {
        setMessage(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
      setIngesting(false);
      ingestAbortRef.current = null;
      try { eventSourceRef.current?.close(); } catch {}
      eventSourceRef.current = null;
      setProgressPercent(0);
      setEtaLabel('');
      startTimeRef.current = 0;
      totalsRef.current = { current: 0, total: 0 };
    }
  };

  const pickFiles = (fileList, autoStart = false) => {
    if (!fileList || fileList.length === 0) return;
    const next = [];
    const rejected = [];
    for (const f of Array.from(fileList)) {
      const ext = (f.name || '').toLowerCase().slice((f.name || '').lastIndexOf('.'));
      if (allowedExtensions.has(ext)) {
        next.push(f);
      } else {
        rejected.push(f.name || 'file sconosciuto');
      }
    }
    if (rejected.length > 0) {
      setUploadError(`File non supportati: ${rejected.join(', ')}`);
    } else {
      setUploadError('');
    }
    setUploadFiles((prev) => {
      const merged = [...prev, ...next];
      const seen = new Set();
      const deduped = merged.filter((f) => {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (autoStart && deduped.length > 0) {
        uploadSelectedFiles(deduped);
      }
      return deduped;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    pickFiles(e.dataTransfer.files, true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInput = (e) => {
    pickFiles(e.target.files, true);
    e.target.value = '';
  };

  const removeUploadFile = (idx) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearUploadSelection = () => {
    setUploadFiles([]);
    setUploadError('');
    setUploadResult(null);
    setUploadProgress(0);
  };

  const uploadSelectedFiles = async (filesOverride) => {
    const filesToSend = filesOverride && filesOverride.length > 0 ? filesOverride : uploadFiles;
    if (filesToSend.length === 0) {
      setUploadError('Seleziona almeno un file.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    setUploadResult(null);
    setMessage('');
    try {
      const form = new FormData();
      filesToSend.forEach((f) => form.append('files', f, f.name));

      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', withTenant('/api/admin/upload', uploadTenantId || undefined), true);
        xhr.upload.onprogress = (evt) => {
          if (!evt.lengthComputable) return;
          const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
          setUploadProgress(pct);
        };
        xhr.onload = () => {
          const ok = xhr.status >= 200 && xhr.status < 300;
          let parsed = {};
          if (xhr.responseText) {
            try {
              parsed = JSON.parse(xhr.responseText);
            } catch {
              parsed = {};
            }
          }
          if (!ok) {
            const detail = parsed.detail || parsed.title || xhr.responseText || 'Upload non riuscito.';
            const err = new Error(`(${xhr.status}) ${detail}`);
            err.code = parsed.code || '';
            err.payload = parsed;
            reject(err);
            return;
          }
          resolve(parsed);
        };
        xhr.onerror = () => reject(new Error('Errore di rete durante l’upload.'));
        xhr.send(form);
      });
      setUploadResult(data);
      setMessage(`Upload completato: ${data.saved?.length || 0} file, elaborati ${data.processed}, skippati ${data.skipped}.`);
      setUploadFiles([]);
      await loadTenantStatsForUpload(uploadTenantId || undefined);
      await loadTenantFilesForUpload(uploadTenantId || undefined);
      await callInfo(uploadTenantId || undefined);
    } catch (err) {
      if (err?.code === 'embedding_schema_mismatch') {
        await handleEmbeddingSchemaMismatch(err?.payload, uploadTenantId || undefined);
        return;
      }
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="chat-container">
      <header className="chat-header">
        <div>
          <h1>Gestione Documenti</h1>
          <p>Comandi e operazioni disponibili sull’archivio locale</p>
        </div>
      </header>

      <main className="chat-content">
        {loading && <div className="loading-inline">Operazione in corso...</div>}
        {message && <div className="info-banner">{message}</div>}

        <div className="tenants-card">
          <div className="upload-header">
            <strong>Gestione tenant</strong>
            <div className="tenant-actions">
              <button className="secondary-button" onClick={loadTenants} disabled={tenantLoading}>
                <SyncOutlined /> Aggiorna
              </button>
            </div>
          </div>

          <div className="form-stack">
            <div className="form-section">
              <div className="form-section-header">
                <strong>Dati tenant</strong>
                <span className="muted">Identificativo e database</span>
              </div>
              <div className="tenant-form-grid">
                <div>
                  <label className="tenant-label">Nome</label>
                  <input
                    className="tenant-input"
                    value={tenantForm.name}
                    onChange={(e) => setTenantForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Università di Bari"
                  />
                </div>
                <div>
                  <label className="tenant-label">Display name</label>
                  <input
                    className="tenant-input"
                    value={tenantForm.displayName}
                    onChange={(e) => setTenantForm((prev) => ({ ...prev, displayName: e.target.value }))}
                    placeholder="UniBA"
                  />
                </div>
                <div>
                  <label className="tenant-label">Database name</label>
                  <input
                    className="tenant-input"
                    value={tenantForm.databaseName}
                    onChange={(e) => setTenantForm((prev) => ({ ...prev, databaseName: e.target.value }))}
                    placeholder="uniba"
                  />
                </div>
                <div>
                  <label className="tenant-label">Tono risposta IA</label>
                  <select
                    className="tenant-input"
                    value={tenantForm.responseTone}
                    onChange={(e) => setTenantForm((prev) => ({ ...prev, responseTone: e.target.value }))}
                  >
                    <option value="neutrale">neutrale</option>
                    <option value="formale">formale</option>
                    <option value="infrormale">infrormale</option>
                  </select>
                </div>
                <div className="tenant-form-dev-option">
                  <label className="tenant-label checkbox-label">
                    <input
                      type="checkbox"
                      checked={tenantForm.isDev}
                      onChange={(e) => setTenantForm((prev) => ({ ...prev, isDev: e.target.checked }))}
                    />
                    <span>Ambiente DEV (solo test)</span>
                  </label>
                  {tenantForm.isDev && (
                    <span className="muted tenant-dev-hint">
                      Il database avrà il suffisso <code>_dev</code>. Il tenant sarà visibile solo in ambiente test e raggiungibile tramite URL test con <code>?cod=GUID</code>.
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-header">
                <strong>Branding</strong>
                <span className="muted">Logo e anteprima</span>
              </div>
              <div className="tenant-form-grid">
                <div>
                  <label className="tenant-label">Logo URL</label>
                  <input
                    className="tenant-input"
                    value={tenantForm.logoUrl}
                    onChange={(e) => setTenantForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="tenant-label">Logo file (JPG/PNG/SVG, max 2MB)</label>
                  <input
                    className="tenant-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCreateLogoFile(file);
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="tenant-logo-preview">
                  <label className="tenant-label">Anteprima logo</label>
                  {tenantLogoPreview ? (
                    <img className="tenant-logo-preview-image" src={tenantLogoPreview} alt="Anteprima logo tenant" />
                  ) : (
                    <span className="muted">Nessuna anteprima disponibile.</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {tenantError && <div className="error-banner">{tenantError}</div>}

          <div className="upload-actions">
            <button className="primary-button" onClick={createTenant} disabled={tenantLoading}>
              <PlusOutlined /> Crea tenant
            </button>
          </div>

          <div className="tenant-grid">
            {tenants.length === 0 && <span className="muted">Nessun tenant registrato.</span>}
            {tenants.map((t) => (
              <div key={t.id} className="tenant-row">
                <div className="tenant-meta" onClick={() => openTenantUpload(t)} style={{ cursor: 'pointer' }}>
                  <div className="tenant-title">
                    {t.displayName || t.name}
                    {(() => {
                      const toneMeta = getToneMeta(t.responseTone);
                      return (
                        <span
                          className={`tenant-tone-icon tenant-tone-${toneMeta.key}`}
                          title={toneMeta.label}
                          aria-label={toneMeta.label}
                        >
                          {toneMeta.icon}
                        </span>
                      );
                    })()}
                    {!t.enabled && <span className="tenant-tag">disattivo</span>}
                    {t.isDev && <span className="tenant-tag tenant-tag-dev">DEV</span>}
                  </div>
                  <div className="tenant-id mono">{t.id}</div>
                </div>
                <div className="tenant-actions tenant-toolbox">
                  {/* 1. Azioni principali */}
                  <button
                    className="secondary-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openTenantUpload(t);
                    }}
                    title="Gestione documenti"
                  >
                    <FileTextOutlined /> Documenti
                  </button>
                  <button
                    className="secondary-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = withTenant('/wizard', t.id);
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    title="Configura wizard immagini"
                  >
                    <CompassOutlined /> Wizard
                  </button>
                  <button
                    className="secondary-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = withTenant('/logs', t.id);
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    title="Visualizza log attività"
                  >
                    <FileSearchOutlined /> Log
                  </button>
                  {/* 2. Configurazione */}
                  <button
                    className="secondary-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditTenant(t);
                    }}
                    disabled={tenantLoading}
                    title="Modifica impostazioni tenant"
                  >
                    <EditOutlined /> Modifica
                  </button>
                  {/* 3. Info e copia */}
                  <button
                    className="secondary-button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const url = getTenantAccessUrl(t);
                      try {
                        await navigator.clipboard.writeText(url);
                        setMessage('URL tenant copiato negli appunti.');
                      } catch {
                        setMessage('Impossibile copiare URL.');
                      }
                    }}
                    title={isTestEnvironmentHost ? 'Copia URL tenant test (?cod=GUID)' : 'Copia URL pubblico'}
                  >
                    <LinkOutlined /> URL
                  </button>
                  <button
                    className="secondary-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyTenantId(t.id);
                    }}
                    title="Copia GUID tenant"
                  >
                    <CopyOutlined /> GUID
                  </button>
                  {/* 4. Gestione stato */}
                  <button
                    className={t.enabled ? 'secondary-button' : 'primary-button'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTenant(t.id, !t.enabled);
                    }}
                    disabled={tenantLoading}
                    title={t.enabled ? 'Disabilita accesso tenant' : 'Abilita accesso tenant'}
                  >
                    {t.enabled ? <><PauseCircleOutlined /> Disabilita</> : <><PlayCircleOutlined /> Abilita</>}
                  </button>
                  {/* 5. Eliminazione - sempre alla fine */}
                  {!t.enabled && (
                    <button
                      className="danger-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteTenant(t);
                      }}
                      disabled={tenantLoading}
                      title="Elimina tenant definitivamente"
                    >
                      <DeleteOutlined /> Elimina
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {showUploadSection && (
          <div className="upload-card">
            <div className="upload-header">
              <strong>
                Caricamento documenti
                {(() => {
                  const t = tenants.find((x) => x.id === uploadTenantId);
                  const name = t?.displayName || t?.name || 'n/d';
                  return name ? ` · ${name}` : '';
                })()}
              </strong>
              <span className="muted">PDF, DOCX, XLSX, XLS</span>
              <div className="tenant-actions">
                <span className="muted">
                  Tenant: {tenants.find((t) => t.id === uploadTenantId)?.displayName || tenants.find((t) => t.id === uploadTenantId)?.name || 'n/d'}
                </span>
                <button className="secondary-button" onClick={() => setShowUploadSection(false)} disabled={uploading}>
                  <CloseOutlined /> Chiudi
                </button>
              </div>
            </div>

            <div className="section-stack">
              {uploadStats && (
                <div className="section-card">
                  <div className="section-card-header">
                    <strong>Stato archivio</strong>
                    <span className="muted">Snapshot tenant</span>
                  </div>
                  <div className="info-box">
                    <div><strong>File archiviati</strong>: {uploadStats.fileCount ?? 0}</div>
                    <div><strong>Chunk</strong>: {uploadStats.chunkCount ?? 0}</div>
                    <div><strong>Dimensione totale</strong>: {uploadStats.totalContentSizeMB ?? 0} MB</div>
                    <div><strong>Documento più recente</strong>: {uploadStats.newestDocument ?? 'n/d'}</div>
                  </div>
                </div>
              )}

              <div className="section-card">
                <div className="section-card-header">
                  <strong>File caricati</strong>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => loadTenantFilesForUpload(uploadTenantId)}
                    disabled={uploadFilesLoading}
                  >
                    <ReloadOutlined />
                  </button>
                </div>
                {uploadFilesError && <div className="error-banner">{uploadFilesError}</div>}
                <div className="chat-files-list">
                  {uploadFilesLoading && <span className="muted">Caricamento...</span>}
                  {!uploadFilesLoading && uploadFilesList.length === 0 && <span className="muted">Nessun file.</span>}
                  {uploadFilesList.map((item) => (
                    <div
                      key={item.filePath || item.sourceFile}
                      className="chat-file-row"
                      style={{ opacity: item.isVisible === false ? 0.65 : 1 }}
                    >
                      <a
                        className="chat-file-item"
                        href={item.fileUrl || withTenant(`/api/files?path=${encodeURIComponent(item.filePath || item.sourceFile || '')}`, uploadTenantId || undefined)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.sourceFile || item.filePath}
                      </a>
                      <label className="tenant-label checkbox-label" style={{ margin: 0, minWidth: 120 }}>
                        <input
                          type="checkbox"
                          checked={item.isVisible === false}
                          onChange={(e) => {
                            const shouldHide = e.target.checked;
                            toggleFileVisibility(item.filePath, !shouldHide, item.sourceFile);
                          }}
                          disabled={updatingVisibilityFile === item.filePath || deletingFile === item.filePath}
                        />
                        <span>Nascondi da fonti</span>
                      </label>
                      <button
                        className="inline-remove"
                        onClick={() => deleteUploadedFile(item.filePath, item.sourceFile)}
                        disabled={deletingFile === item.filePath || updatingVisibilityFile === item.filePath}
                        title="Elimina file"
                      >
                        {deletingFile === item.filePath ? <LoadingOutlined spin /> : <DeleteOutlined />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="section-card">
                <div className="section-card-header">
                  <strong>Carica nuovi file</strong>
                  <span className="muted">Drag & drop o selezione</span>
                </div>
                <div
                  className={`dropzone ${dragActive ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    id="upload-input"
                    type="file"
                    multiple
                    accept=".pdf,.docx,.xlsx,.xls"
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                    disabled={uploading || ingesting}
                  />
                  <label htmlFor="upload-input" className="dropzone-label">
                    <div className="dropzone-title">Trascina qui i file</div>
                    <div className="dropzone-subtitle">oppure clicca per selezionare</div>
                  </label>
                </div>

                {uploadFiles.length > 0 && (
                  <div className="upload-files">
                    {uploadFiles.map((f, i) => (
                      <div key={`${f.name}-${f.lastModified}`} className="upload-file-item">
                        <span className="mono">{f.name}</span>
                        <button className="inline-remove" onClick={() => removeUploadFile(i)} disabled={uploading}>
                          <CloseOutlined />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadError && <div className="error-banner">{uploadError}</div>}

                <div className="upload-actions">
                  <button className="primary-button" onClick={uploadSelectedFiles} disabled={uploading || ingesting}>
                    {uploading ? 'Caricamento...' : 'Carica e acquisisci'}
                  </button>
                  <button className="secondary-button" onClick={clearUploadSelection} disabled={uploading || uploadFiles.length === 0}>
                    Svuota selezione
                  </button>
                </div>

                {uploading && (
                  <div className="upload-progress">
                    <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                    <span className="upload-progress-label">{uploadProgress}%</span>
                  </div>
                )}

                {uploadResult?.skippedFiles?.length > 0 && (
                  <div className="skipped-files-box">
                    <strong style={{ color: '#f8fafc' }}>File skippati in upload:</strong>
                    <div className="skipped-files-list">
                      {uploadResult.skippedFiles.map((file, i) => (
                        <div key={i} className="skipped-file-item">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span className="mono" style={{ fontWeight: 600, color: '#e2e8f0' }}>{file.fileName}</span>
                            <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{file.reason}</span>
                            <span className="mono" style={{ fontSize: '0.75rem', color: '#64748b' }}>{file.filePath}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {showSkippedFiles && skippedFiles.length > 0 && (
          <div className="skipped-files-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong style={{ color: '#f8fafc' }}>File skippati ({skippedFiles.length}):</strong>
              <button className="secondary-button" onClick={() => setShowSkippedFiles(false)} style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                Nascondi
              </button>
            </div>
            <div className="skipped-files-list">
              {skippedFiles.map((file, i) => (
                <div key={i} className="skipped-file-item">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="mono" style={{ fontWeight: 600, color: '#e2e8f0' }}>{file.fileName}</span>
                    <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{file.reason}</span>
                    <span className="mono" style={{ fontSize: '0.75rem', color: '#64748b' }}>{file.filePath}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!showSkippedFiles && skippedFiles.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <button className="secondary-button" onClick={() => setShowSkippedFiles(true)} style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
              Mostra file skippati ({skippedFiles.length})
            </button>
          </div>
        )}
      </main>

      {pathModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Seleziona cartella documenti</h3>
            <p>Scegli una cartella dal filesystem.</p>
            <div className="folder-browser">
              <div className="browser-list">
                {browseParent && (
                  <button className="browser-item" onClick={() => goInto(browseParent)}>
                    <FolderOpenOutlined /> ..
                  </button>
                )}
                {browseEntries.map((e) => (
                  <button key={e.path} className="browser-item" onClick={() => goInto(e.path)}>
                    <FolderOpenOutlined /> {e.name || e.path}
                  </button>
                ))}
                {browseEntries.length === 0 && (
                  <div className="browser-empty">Nessuna sottocartella.</div>
                )}
              </div>
              <div className="selected-list">
                <div className="selected-actions">
                  <button className="secondary-button" onClick={addCurrentToSelection} disabled={!browsePath}>Aggiungi cartella corrente</button>
                  {selectedPaths.length > 0 && (
                    <span className="muted">{selectedPaths.length} selezionata/e</span>
                  )}
                </div>
                {selectedPaths.length > 0 && (
                  <div className="paths-list">
                    {selectedPaths.map((p) => (
                      <div key={p} className="path-item">
                        <span className="mono">{p}</span>
                        <button className="inline-remove" onClick={() => removeSelectedPath(p)}><CloseOutlined /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setPathModalOpen(false)}>Annulla</button>
              <button className="primary-button" onClick={confirmIngestWithPath} disabled={!browsePath && selectedPaths.length === 0}>
                {addOnly ? 'Aggiungi percorso' : 'Avvia acquisizione'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Modifica tenant</h3>
            <p>Aggiorna i dati del tenant (il database non è modificabile).</p>

            <div className="form-stack">
              <div className="form-section">
                <div className="form-section-header">
                  <strong>Dati tenant</strong>
                  <span className="muted">Identificativo</span>
                </div>
                <div className="tenant-form-grid">
                  <div>
                    <label className="tenant-label">Nome (interno)</label>
                    <input
                      className="tenant-input"
                      value={editForm.name}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="uniba"
                    />
                  </div>
                  <div>
                    <label className="tenant-label">Nome visualizzato</label>
                    <input
                      className="tenant-input"
                      value={editForm.displayName}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, displayName: e.target.value }))}
                      placeholder="UniBA"
                    />
                  </div>
                  <div>
                    <label className="tenant-label">Tono risposta IA</label>
                    <select
                      className="tenant-input"
                      value={editForm.responseTone}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, responseTone: e.target.value }))}
                    >
                      <option value="neutrale">neutrale</option>
                      <option value="formale">formale</option>
                      <option value="infrormale">infrormale</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-section">
                <div className="form-section-header">
                  <strong>Branding</strong>
                  <span className="muted">Logo e anteprima</span>
                </div>
                <div className="tenant-form-grid">
                  <div>
                    <label className="tenant-label">Logo URL</label>
                    <input
                      className="tenant-input"
                      value={editForm.logoUrl}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="tenant-label">Logo file (JPG/PNG/SVG, max 2MB)</label>
                    <input
                      className="tenant-input"
                      type="file"
                      accept=".jpg,.jpeg,.png,.svg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleEditLogoFile(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <div className="tenant-logo-preview">
                    <label className="tenant-label">Anteprima logo</label>
                    {editLogoPreview ? (
                      <img className="tenant-logo-preview-image" src={editLogoPreview} alt="Anteprima logo tenant" />
                    ) : (
                      <span className="muted">Nessuna anteprima disponibile.</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-section form-section-inline">
                <input
                  id="tenant-enabled"
                  type="checkbox"
                  checked={!!editForm.enabled}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
                <label htmlFor="tenant-enabled" className="tenant-label" style={{ margin: 0 }}>
                  Tenant abilitato
                </label>
              </div>
            </div>

            {tenantError && <div className="error-banner">{tenantError}</div>}

            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={() => {
                  setEditOpen(false);
                  setEditTenantId('');
                }}
                disabled={tenantLoading}
              >
                Annulla
              </button>
              <button className="primary-button" onClick={saveTenantEdits} disabled={tenantLoading}>
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Elimina tenant</h3>
            <p>
              Questa operazione elimina il tenant, i file in <span className="mono">uploads/{deleteTenantId}</span> e il database.
            </p>
            <p>Per confermare, inserisci la GUID del tenant selezionato:</p>
            <input
              className="tenant-input"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              placeholder="GUID tenant"
            />

            {tenantError && <div className="error-banner">{tenantError}</div>}

            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeleteTenantId('');
                  setDeleteConfirmInput('');
                }}
                disabled={tenantLoading}
              >
                Annulla
              </button>
              <button className="danger-button" onClick={confirmDeleteTenant} disabled={tenantLoading}>
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmIngestOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Avvio acquisizione</h3>
            <p>
              {`Vuoi che inizi l'acquisizione di ${info?.totalFiles ?? 0} documenti?`}
            </p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setConfirmIngestOpen(false)}>Annulla</button>
              <button className="primary-button" onClick={startIngestionFromConfigured} disabled={ingesting}>Avvia acquisizione</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


